
import { defineComponent } from "vue";
import { listConfigs, upsertConfig } from "@/services/api/configApi";
import { formatDateToUTC } from "@/utils/format";
import BaseButton from "@/components/ui/BaseButton.vue";
import StateBlock from "@/components/ui/StateBlock.vue";
import PanelNoAccess from "@/components/panel/PanelNoAccess.vue";
import { panelRequest, isForbidden } from "@/components/panel/panelAccess";
import { confirm, toast } from "@/composables/ui/useDialogs";
import {
  CONFIG_GROUPS, ConfigEntry, ConfigGroup, editorOf, flagDefault, flagState, groupOf, i18nKeyOf, isChanged,
  isFlag, isJsonFlag, prettyJson, validateDraft, withFlagState,
} from "@/components/panel/config/configCatalog";

type Filter = "all" | "on" | "off" | "changed";

export default defineComponent({
  name: "ConfigManagerComponent",
  components: { PanelNoAccess, StateBlock, BaseButton },
  data() {
    return {
      configs: [] as ConfigEntry[],
      loading: false,
      forbidden: false,
      query: "",
      filter: "all" as Filter,
      filters: ["all", "on", "off", "changed"] as Filter[],
      busy: {} as Record<string, boolean>,
      openKey: "",
      draft: "",
    };
  },
  computed: {
    flags(): ConfigEntry[] {
      return this.configs.filter(isFlag);
    },
    flagsOn(): number {
      return this.flags.filter(flagState).length;
    },
    changedCount(): number {
      return this.configs.filter(isChanged).length;
    },
    visibleFlagGroups(): Array<{ group: ConfigGroup; items: ConfigEntry[] }> {
      const items = this.flags.filter((c) => this.matches(c) && this.passesFilter(c));
      const sorted = [...items].sort((a, b) => a.key.localeCompare(b.key));
      return CONFIG_GROUPS.map((group) => ({ group, items: sorted.filter((c) => groupOf(c.key) === group) })).filter(
        (g) => g.items.length > 0
      );
    },
    visibleAdvanced(): ConfigEntry[] {
      return this.configs
        .filter((c) => !isPlainFlagEntry(c) && this.matches(c) && (this.filter === "all" || this.filter === "changed" ? this.passesFilter(c) : false))
        .sort((a, b) => a.key.localeCompare(b.key));
    },
    draftError(): string {
      const c = this.configs.find((x) => x.key === this.openKey);
      return c ? validateDraft(editorOf(c), this.draft) : "";
    },
  },
  async mounted() {
    await this.loadConfigs();
  },
  methods: {
    flagState,
    isChanged,
    isJsonFlag,
    editorOf,
    prettyJson,
    matches(c: ConfigEntry): boolean {
      const q = this.query.trim().toLowerCase();
      if (!q) return true;
      return [c.key, c.description || "", this.displayName(c)].some((s) => s.toLowerCase().includes(q));
    },
    passesFilter(c: ConfigEntry): boolean {
      if (this.filter === "changed") return isChanged(c);
      if (this.filter === "on") return flagState(c);
      if (this.filter === "off") return !flagState(c);
      return true;
    },
    displayName(c: ConfigEntry): string {
      const path = `panel.config.names.${i18nKeyOf(c.key)}`;
      return this.$te(path) ? this.$t(path) : c.key;
    },
    descriptionOf(c: ConfigEntry): string {
      const path = `panel.config.descriptions.${i18nKeyOf(c.key)}`;
      return this.$te(path) ? this.$t(path) : c.description || "";
    },
    defaultLabel(c: ConfigEntry): string {
      const d = flagDefault(c);
      return d === null ? "" : d ? this.$t("panel.config.on") : this.$t("panel.config.off");
    },
    preview(value: string, max = 60): string {
      const v = (value || "").replace(/\s+/g, " ").trim();
      return v.length > max ? v.slice(0, max) + "…" : v;
    },
    formatDate(date: Date | string | number) {
      return formatDateToUTC(date, (this as any).$i18n?.locale);
    },
    toggleOpen(c: ConfigEntry) {
      if (this.openKey === c.key) {
        this.openKey = "";
        return;
      }
      this.openKey = c.key;
      this.draft = editorOf(c) === "json" ? prettyJson(c.value) : c.value;
    },
    /** Short, truncated form of a value for the confirmation text. */
    show(value: string): string {
      return value === "" ? this.$t("panel.config.empty_value") : this.preview(value, 80);
    },
    async apply(c: ConfigEntry, next: string, from: string, labels?: [string, string]): Promise<boolean> {
      const ok = await confirm({
        title: this.$t("panel.config.confirmTitle", { key: c.key }),
        text:
          `${labels ? labels[0] : this.show(from)} → ${labels ? labels[1] : this.show(next)}` +
          (c.key === "protected_report_whitelist_enabled" && next === "true" ? `\n\n${this.$t("panel.config.whitelistWarning")}` : ""),
        confirmText: this.$t("panel.config.confirmApply"),
        cancelText: this.$t("panel.config.cancel"),
      });
      if (!ok) return false;
      this.busy = { ...this.busy, [c.key]: true };
      try {
        await upsertConfig(
          { key: c.key, value: next, description: c.description, type: c.type || "string" },
          panelRequest
        );
        c.value = next;
        c.updatedAt = new Date().toISOString();
        toast(this.$t("panel.config.saved", { key: c.key }), "success");
        return true;
      } catch (error) {
        console.error("Failed to save config:", error);
        toast(this.$t("panel.config.saveFailed", { key: c.key }), "error", 4000);
        return false;
      } finally {
        this.busy = { ...this.busy, [c.key]: false };
      }
    },
    toggleFlag(c: ConfigEntry) {
      const was = flagState(c);
      const label = (on: boolean) => this.$t(on ? "panel.config.on" : "panel.config.off");
      return this.apply(c, withFlagState(c, !was), c.value, [label(was), label(!was)]).then(() => undefined);
    },
    async saveDraft(c: ConfigEntry) {
      if (this.draftError) return;
      const next = editorOf(c) === "json" ? JSON.stringify(JSON.parse(this.draft)) : this.draft;
      if (await this.apply(c, next, c.value)) this.openKey = "";
    },
    async resetEntry(c: ConfigEntry) {
      const def = c.defaultValue ?? "";
      if (await this.apply(c, def, c.value) && this.openKey === c.key) this.openKey = "";
    },
    async loadConfigs() {
      this.loading = true;
      try {
        this.configs = (await listConfigs(panelRequest)) as ConfigEntry[];
      } catch (error) {
        if (isForbidden(error)) this.forbidden = true;
        else {
          console.error("Failed to load configs:", error);
          toast(this.$t("panel.config.loadFailed"), "error", 4000);
        }
      } finally {
        this.loading = false;
      }
    },
  },
});

/** Advanced list excludes plain true/false flags only; JSON flags can still edit their other fields there. */
function isPlainFlagEntry(c: ConfigEntry): boolean {
  return isFlag(c) && !isJsonFlag(c);
}
